import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, labelStyle, pillButton, outlineButton, ghostButton } from './adminTheme';
import { uploadToB2 } from '../utils/uploadToB2';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

// Défini localement (plutôt qu'importé d'adminTheme.js) pour que ce fichier
// reste indépendant de la version d'adminTheme.js actuellement déployée —
// certains déploiements n'ont pas encore l'export `chipButton`.
const chipButton = {
    padding: '6px 12px',
    background: 'none',
    border: `1px solid ${theme.border}`,
    color: theme.text,
    borderRadius: 0,
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: font,
};

const POSITIONS = {
    fr: [
        { value: 'bottom-left', label: '↙ Bas gauche' },
        { value: 'bottom-1-3', label: '↓ Bas 1/3 gauche' },
        { value: 'bottom-2-3', label: '↓ Bas 2/3 (droite)' },
        { value: 'bottom-right', label: '↘ Bas droite' },
        { value: 'middle-left', label: '← Milieu gauche' },
        { value: 'middle-right', label: '→ Milieu droite' },
        { value: 'top-left', label: '↖ Haut gauche' },
        { value: 'top-1-3', label: '↑ Haut 1/3 gauche' },
        { value: 'top-2-3', label: '↑ Haut 2/3 (droite)' },
        { value: 'top-right', label: '↗ Haut droite' },
    ],
    en: [
        { value: 'bottom-left', label: '↙ Bottom left' },
        { value: 'bottom-1-3', label: '↓ Bottom 1/3 left' },
        { value: 'bottom-2-3', label: '↓ Bottom 2/3 (right)' },
        { value: 'bottom-right', label: '↘ Bottom right' },
        { value: 'middle-left', label: '← Middle left' },
        { value: 'middle-right', label: '→ Middle right' },
        { value: 'top-left', label: '↖ Top left' },
        { value: 'top-1-3', label: '↑ Top 1/3 left' },
        { value: 'top-2-3', label: '↑ Top 2/3 (right)' },
        { value: 'top-right', label: '↗ Top right' },
    ],
};

const T = {
    fr: {
        heading: 'Teaser vidéo (coin inférieur gauche)',
        active: '· actif',
        desc: 'Dessin cliquable qui ouvre le premier film de la campagne active.',
        image: 'Image',
        uploading: 'Upload en cours...',
        position: 'Position',
        save: 'Sauvegarder',
        saving: 'Sauvegarde...',
        activate: 'Activer',
        deactivate: 'Désactiver',
        loading: 'Chargement...',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        imageUploaded: 'Image uploadée',
        saveError: (msg) => `Erreur : ${msg}`,
        activated: 'Teaser activé',
        savedDisabled: 'Sauvegardé (désactivé)',
    },
    en: {
        heading: 'Video teaser (bottom-left corner)',
        active: '· active',
        desc: 'Clickable drawing that opens the active campaign\'s first film.',
        image: 'Image',
        uploading: 'Uploading...',
        position: 'Position',
        save: 'Save',
        saving: 'Saving...',
        activate: 'Activate',
        deactivate: 'Deactivate',
        loading: 'Loading...',
        uploadError: (msg) => `Upload error: ${msg}`,
        imageUploaded: 'Image uploaded',
        saveError: (msg) => `Error: ${msg}`,
        activated: 'Teaser activated',
        savedDisabled: 'Saved (disabled)',
    },
};

function AdminVideoTeaser() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const positions = POSITIONS[lang] || POSITIONS.fr;
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ image_url: '', enabled: false, position: 'bottom-left' });
    const fetchTeaser = async () => {
        setLoading(true);
        const { data } = await supabase.from('video_teaser').select('*').maybeSingle();
        if (data) setForm({ image_url: data.image_url || '', enabled: data.enabled || false, position: data.position || 'bottom-left' });
        setLoading(false);
    };

    useEffect(() => { fetchTeaser(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fileName = `video-teaser-${Date.now()}.${file.name.split('.').pop()}`;
            const url = await uploadToB2(`covers/${fileName}`, file);
            setForm(f => ({ ...f, image_url: url }));
            setStatusMsg(t.imageUploaded);
        } catch (err) {
            setStatusMsg(t.uploadError(err.message));
        }
        setUploading(false);
    };

    const handleSave = async (publish) => {
        setSaving(true);
        const payload = { image_url: form.image_url, enabled: publish, position: form.position };
        const { data: existing } = await supabase.from('video_teaser').select('*').limit(1).maybeSingle();

        let error;
        if (existing) ({ error } = await supabase.from('video_teaser').update(payload).eq('id', existing.id));
        else ({ error } = await supabase.from('video_teaser').insert(payload));

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setForm(f => ({ ...f, enabled: publish }));
            setStatusMsg(publish ? t.activated : t.savedDisabled);
        }
        setSaving(false);
    };

    if (loading) return <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>;

    return (
        <div style={{ maxWidth: '400px' }}>
            <p style={{ ...labelStyle, marginBottom: '4px' }}>
                {t.heading} {form.enabled && <span style={{ color: theme.textMuted }}>{t.active}</span>}
            </p>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.desc}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                    <label style={labelStyle}>{t.image}</label>
                    {form.image_url && (
                        <div style={{ width: '80px', height: '80px', marginBottom: '8px', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={form.image_url} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                        </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                    {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                </div>
                <div>
                    <label style={labelStyle}>{t.position}</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        {positions.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => setForm(f => ({ ...f, position: opt.value }))}
                                style={{
                                    ...chipButton,
                                    background: form.position === opt.value ? theme.text : 'none',
                                    color: form.position === opt.value ? '#fff' : theme.text,
                                    fontSize: '10.5px',
                                }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="wlink" onClick={() => handleSave(false)} disabled={saving} style={outlineButton}>{t.save}</button>
                    <button className="wlink" onClick={() => handleSave(true)} disabled={saving} style={pillButton}>
                        {saving ? t.saving : t.activate}
                    </button>
                </div>

                {form.enabled && <button className="wlink" onClick={() => handleSave(false)} style={ghostButton}>{t.deactivate}</button>}

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
            </div>
        </div>
    );
}

export default AdminVideoTeaser;